// Client-side booking total for the embedded booking widget (and the client / Guide Profile
// checkouts that share it). Moved out of Embedded/ExperienceBooking/BookingDetailCard so the new
// widget prices bookings exactly like the old one. Pure functions only: no component state.
//
// Breakdown lines keep their original `label` / `amount` strings (the old widget renders them as
// is) and also carry `kind`, `count` and a numeric `value` so other screens can word them their own
// way. kind: "flat_rate" | "extra_adult" | "extra_child" | "extra_infant" | "adult" | "child" |
// "infant" | "passenger" | "ticket" | "add_on" | "fee" | "tax_and_fees" | "card_processing_fee".
import { numberToCurrency } from "helpers/string"
import { computeDiscountAmount } from "utils/discountUtils"
import { summarizeAddOnFees } from "components/bookings/helpers"
import { getCardFixedFee } from "utils/bookingCalculations"
import {
  applyPriceAdjustments,
  getApplicableDayAdjustments,
  getApplicableSeasonalAdjustments
} from "pagesV2/pro/Bookings/helpers/calculateBookingTotal"

const EMPTY_COUNTS = { adults: 0, children: 0, infants: 0, passengers: 0 }

const buildLine = ({ kind, label, value, count, ...rest }) => ({
  kind,
  label,
  amount: numberToCurrency(value),
  value,
  ...(count != null && { count }),
  ...rest
})

const getApplicableAdjustments = ({ dayAdjustments, seasonalAdjustments, availableDateRanges, date }) => [
  ...getApplicableDayAdjustments(dayAdjustments, date),
  ...getApplicableSeasonalAdjustments(seasonalAdjustments, date, availableDateRanges)
]

const computeAdjustedRates = (priceData, applicableAdjustments) => {
  const { flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger } = priceData
  const adjustPrice = (price) => applyPriceAdjustments(price, applicableAdjustments)
  return {
    flatAmount: adjustPrice(flat_rate),
    pricePerPerson: adjustPrice(price_per_person),
    pricePerChild: adjustPrice(price_per_child),
    pricePerInfant: adjustPrice(price_per_infant),
    pricePerPassenger: adjustPrice(price_per_passenger)
  }
}

// Unit prices of one trip price on `date` after its day and seasonal adjustments — the same rates
// calculateWidgetBookingTotal bills with. { flatAmount, pricePerPerson, pricePerChild,
// pricePerInfant, pricePerPassenger }
export const getAdjustedRates = ({ priceData = {}, date, dayAdjustments, seasonalAdjustments, availableDateRanges }) =>
  computeAdjustedRates(priceData, getApplicableAdjustments({ dayAdjustments, seasonalAdjustments, availableDateRanges, date }))

// Splits guestsIncluded capacity across adults -> children -> infants (in that priority order) and
// returns each group's "extra" (billed) count once the included capacity is exhausted.
export const allocateIncludedCapacity = (totalCounts, guestsIncluded) => {
  let remainingCapacity = guestsIncluded
  const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)
  const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)
  const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)
  return {
    extraAdults: Math.max(0, totalCounts.adults - adultsIncluded),
    extraChildren: Math.max(0, totalCounts.children - childrenIncluded),
    extraInfants: Math.max(0, totalCounts.infants - infantsIncluded)
  }
}

const computeFlatRateBreakdown = ({ totalCounts, rates, includedGuests }) => {
  const { flatAmount, pricePerPerson, pricePerChild, pricePerInfant } = rates
  const totalGuests = totalCounts.adults + totalCounts.children + totalCounts.infants + totalCounts.passengers
  const hasGuests = totalGuests > 0 || totalCounts.passengers > 0
  if (!hasGuests) return { total: 0, breakdown: [] }

  let total = flatAmount
  const breakdown = [
    buildLine({
      kind: "flat_rate",
      label: "Flat Rate",
      value: flatAmount,
      count: Math.min(totalGuests, includedGuests),
      pricingDetails: {
        pricing_type: "charge_flat_rate",
        included_guests: includedGuests,
        price_per_person: pricePerPerson,
        price_per_child: pricePerChild,
        price_per_infant: pricePerInfant
      }
    })
  ]

  const { extraAdults, extraChildren, extraInfants } = allocateIncludedCapacity(totalCounts, includedGuests)

  // Extra guests of every type are billed at the per-person rate.
  const extraLines = [
    { kind: "extra_adult", label: "Extra Adult", count: extraAdults },
    { kind: "extra_child", label: "Extra Child", count: extraChildren },
    { kind: "extra_infant", label: "Extra Infant", count: extraInfants },
    { kind: "passenger", label: "Passenger", count: totalCounts.passengers }
  ]
  for (const { kind, label, count } of extraLines) {
    if (count > 0) {
      const lineTotal = count * pricePerPerson
      total += lineTotal
      breakdown.push(buildLine({ kind, label: `${label} x ${count}`, value: lineTotal, count }))
    }
  }

  return { total, breakdown }
}

const computeCountBreakdown = ({ totalCounts, rates }) => {
  const mapping = [
    { kind: "adult", count: totalCounts.adults, label: "Adult", price: rates.pricePerPerson },
    { kind: "child", count: totalCounts.children, label: "Child", price: rates.pricePerChild },
    { kind: "infant", count: totalCounts.infants, label: "Infant", price: rates.pricePerInfant },
    { kind: "passenger", count: totalCounts.passengers, label: "Passenger", price: rates.pricePerPassenger }
  ]
  let total = 0
  const breakdown = []
  for (const { kind, count, label, price } of mapping) {
    if (count > 0) {
      const lineTotal = count * price
      total += lineTotal
      breakdown.push(buildLine({ kind, label: `${label} x ${count}`, value: lineTotal, count }))
    }
  }
  return { total, breakdown }
}

const computeGuestPricingBreakdown = ({ pricingType, totalCounts, rates, includedGuests }) => {
  if (pricingType === "charge_flat_rate") {
    return computeFlatRateBreakdown({ totalCounts, rates, includedGuests })
  }
  return computeCountBreakdown({ totalCounts, rates })
}

const computeAddOnBreakdown = (addOnSelections, tripAddOns) => {
  let addOnsTotal = 0
  const breakdown = []
  for (const addOn of tripAddOns) {
    const qty = Number(addOnSelections[addOn.id] || 0)
    if (qty > 0) {
      const lineTotal = qty * Number(addOn.price ?? 0)
      addOnsTotal += lineTotal
      breakdown.push(buildLine({ kind: "add_on", label: `${addOn.title} × ${qty}`, value: lineTotal, count: qty, addOnId: addOn.id, title: addOn.title }))
    }
  }
  return { addOnsTotal, breakdown }
}

const computeDiscountTotal = ({ discountPromos, guestSubtotal, addOnsTotal }) => {
  const rawDiscount = (discountPromos ?? []).reduce((sum, promo) => sum + computeDiscountAmount(promo, guestSubtotal, addOnsTotal), 0)
  return Math.max(0, Math.min(rawDiscount, guestSubtotal + addOnsTotal))
}

const resolveFeeEntries = (customFees, cardFeePassthroughEnabled) => ({
  cardFeeEntry: cardFeePassthroughEnabled ? customFees.find((fee) => fee.title === "Card Processing Fee") : null,
  platformFeeEntry: customFees.find((fee) => fee.title === "Platform Fee"),
  otherFees: customFees.filter((fee) => fee.title !== "Card Processing Fee" && fee.title !== "Platform Fee")
})

// Platform fee (commission): guest portion at the standard rate + per-add-on commission (each at
// its own commission_percentage, or the standard rate when null).
const computePlatformFeeAmount = ({ platformFeeEntry, guestSubtotal, discountFactor, commissionAddOns }) => {
  if (!platformFeeEntry) return 0
  const platformRate = Number(platformFeeEntry.price) || 0
  const guestPortion = (guestSubtotal * discountFactor * platformRate) / 100
  const addOnPortion = commissionAddOns.reduce((sum, addOn) => {
    const rate = addOn.commissionPercentage != null ? addOn.commissionPercentage : platformRate
    return sum + (addOn.amount * discountFactor * rate) / 100
  }, 0)
  return guestPortion + addOnPortion
}

// Other custom fees: percentage fees apply to the guest subtotal plus opted-in add-ons; fixed fees
// stay flat (charged once).
const buildOtherFeeItems = ({ otherFees, customFeeBase }) =>
  otherFees.map((fee) => ({
    fee,
    amount: fee.fee_type === "percentage" ? (customFeeBase * Number(fee.price)) / 100 : Number(fee.price)
  }))

// Card processing fee applied last. It's charged only on the amount paid by card NOW: the deposit
// (depositFraction of the pre-card total) for deposit payments, or the whole pre-card total when
// paying in full. `total` at this point is the pre-card total.
// Gross-up: divide by (1 - rate) so that after Stripe deducts its fee on (depositBase + fee), the
// net equals depositBase. A plain markup would under-collect by the fee-on-fee. `cardFeeShare` is
// this booking's share of the entry's fixed fee (getFixedCardFeeShares).
const computeCardProcessingFee = ({ cardFeeEntry, total, depositFraction, cardFeeShare }) => {
  if (!cardFeeEntry || total <= 0) return 0
  const cardRate = Number(cardFeeEntry.price) / 100
  const depositBaseForFee = total * depositFraction
  return (depositBaseForFee * cardRate + getCardFixedFee(cardFeeEntry) * cardFeeShare) / (1 - cardRate)
}

// `isCardFeeListed` false: the processing fee stays out of the list (and out of "Tax & Fees"), for a
// screen that shows it below the Total.
const buildFeesBreakdown = ({ hiddenFeesTotal, cardFeeEntry, cardProcessingFee, isCardFeeListed }) => {
  const entries = []
  const listedCardFee = isCardFeeListed ? cardProcessingFee : 0
  const taxAndFeesTotal = hiddenFeesTotal + (cardFeeEntry && !cardFeeEntry.show_as_line_item ? listedCardFee : 0)
  if (taxAndFeesTotal > 0) {
    entries.push(buildLine({ kind: "tax_and_fees", label: "Tax & Fees", value: taxAndFeesTotal }))
  }
  if (cardFeeEntry?.show_as_line_item && listedCardFee > 0) {
    entries.push(buildLine({ kind: "card_processing_fee", label: "Card Processing Fee", value: listedCardFee }))
  }
  return entries
}

// Everything after the guests are priced, shared by private and ticketed bookings: add-ons, the
// promo discount, the custom fees and taxes, and the card processing fee.
const addAddOnsDiscountAndFees = ({
  guestTotal,
  guestBreakdown,
  addOnSelections,
  tripAddOns,
  customFees,
  discountPromos,
  cardFeePassthroughEnabled,
  depositFraction,
  cardFeeShare,
  isCardFeeListed
}) => {
  let total = guestTotal
  const breakdown = [...guestBreakdown]

  // Guest-only subtotal (used as discount base when promo does NOT apply to add-ons)
  const guestSubtotal = total

  const { addOnsTotal, breakdown: addOnBreakdown } = computeAddOnBreakdown(addOnSelections || {}, tripAddOns || [])
  total += addOnsTotal
  breakdown.push(...addOnBreakdown)

  // Apply the discount to the pre-tax base BEFORE computing tax & fees, matching the backend.
  // Each promo's apply_to_add_ons flag decides whether add-ons are part of its discountable base.
  const discountAmount = computeDiscountTotal({ discountPromos, guestSubtotal, addOnsTotal })
  total -= discountAmount

  // Spread the discount across every taxable portion so each fee base is reduced proportionally
  // even though add-on portions can now be taxed at different rates.
  const grossBase = guestSubtotal + addOnsTotal
  const discountFactor = grossBase > 0 ? (grossBase - discountAmount) / grossBase : 1
  const netTaxableBase = grossBase - discountAmount

  // Per-add-on fee participation: the platform fee (commission_applicable/commission_percentage)
  // and the custom percentage fees (apply_custom_tax_and_fees). The card processing fee always
  // applies to every add-on (it's charged on the whole running total below).
  const { customTaxableAddOnsTotal, commissionAddOns } = summarizeAddOnFees(addOnSelections || {}, tripAddOns || [])

  let cardProcessingFee = 0

  // "Card Processing Fee" is applied last on the running total. The "Platform Fee" entry is the
  // standard commission and is charged on the guest subtotal plus any commission-applicable
  // add-ons. All other custom fees include only add-ons that opted in.
  if (netTaxableBase > 0 && customFees?.length > 0) {
    const { cardFeeEntry, platformFeeEntry, otherFees } = resolveFeeEntries(customFees, cardFeePassthroughEnabled)

    const platformTaxAmount = computePlatformFeeAmount({ platformFeeEntry, guestSubtotal, discountFactor, commissionAddOns })
    const customFeeBase = (guestSubtotal + customTaxableAddOnsTotal) * discountFactor
    const otherFeeItems = buildOtherFeeItems({ otherFees, customFeeBase })
    const feeContributions = [{ fee: platformFeeEntry, amount: platformTaxAmount }, ...otherFeeItems]

    let hiddenFeesTotal = 0
    for (const { fee, amount } of feeContributions) {
      if (amount <= 0) continue
      total += amount
      if (fee.show_as_line_item) {
        breakdown.push(buildLine({ kind: "fee", label: fee.title, value: amount }))
      } else {
        hiddenFeesTotal += amount
      }
    }

    cardProcessingFee = computeCardProcessingFee({ cardFeeEntry, total, depositFraction, cardFeeShare })
    total += cardProcessingFee

    breakdown.push(...buildFeesBreakdown({ hiddenFeesTotal, cardFeeEntry, cardProcessingFee, isCardFeeListed }))
  }

  return { total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal: total - cardProcessingFee }
}

/**
 * Prices one widget booking.
 *
 * @param {object} params
 * @param {object} params.priceData            the selected trip price (trip_prices[] entry)
 * @param {string} params.date                 booking (start) date, YYYY-MM-DD; adjustments use it
 * @param {object} params.counts               { main: { adults, children, infants, passengers } }
 * @param {object} params.addOnSelections      { [addOnId]: qty }
 * @param {Array}  params.tripAddOns           the trip's add_ons
 * @param {Array}  params.dayAdjustments       experience.day_adjustments
 * @param {Array}  params.seasonalAdjustments  experience.seasonal_adjustments
 * @param {Array}  params.availableDateRanges  experience.available_date_ranges
 * @param {Array}  params.customFees           experience.assigned_custom_fees
 * @param {Array}  params.discountPromos       applied promo codes
 * @param {boolean} params.cardFeePassthroughEnabled  platform_fee_setting.card_fee_passthrough_enabled
 * @param {number} params.depositFraction      share of the pre-card total paid by card now (getCardFeeFraction)
 * @param {number} params.cardFeeShare         this booking's share of the card fee's fixed part: 1 alone, a share in an order
 * @param {boolean} params.isCardFeeListed     false keeps the processing fee out of the breakdown (shown below the Total)
 * @returns {{ total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal }}
 */
export const calculateWidgetBookingTotal = ({
  priceData = {},
  date,
  counts,
  addOnSelections = {},
  tripAddOns = [],
  dayAdjustments,
  seasonalAdjustments,
  availableDateRanges,
  customFees = [],
  discountPromos = [],
  cardFeePassthroughEnabled = false,
  depositFraction = 1,
  cardFeeShare = 1,
  isCardFeeListed = true
}) => {
  const { pricing_type, included_guests, max_guests } = priceData
  const includedGuests = included_guests ?? max_guests ?? 0

  // Fold any matching day adjustments (e.g. weekend surcharges) and seasonal adjustments
  // (e.g. a holiday range) directly into the base unit prices before computing totals, so they
  // apply to the flat rate / per-person rates and to extra-guest per-person rates alike.
  // Both kinds stack: each is computed off the base price and the deltas are summed.
  const applicableAdjustments = getApplicableAdjustments({ dayAdjustments, seasonalAdjustments, availableDateRanges, date })
  const rates = computeAdjustedRates(priceData, applicableAdjustments)

  const totalCounts = counts?.main || EMPTY_COUNTS

  const { total: guestTotal, breakdown: guestBreakdown } = computeGuestPricingBreakdown({
    pricingType: pricing_type,
    totalCounts,
    rates,
    includedGuests
  })
  return addAddOnsDiscountAndFees({
    guestTotal,
    guestBreakdown,
    addOnSelections,
    tripAddOns,
    customFees,
    discountPromos,
    cardFeePassthroughEnabled,
    depositFraction,
    cardFeeShare,
    isCardFeeListed
  })
}

const getApplicableTicketAdjustments = ({ dayAdjustments, seasonalAdjustments, availableDateRanges, date, ticketTypeId }) => [
  ...getApplicableDayAdjustments(dayAdjustments, date, ticketTypeId),
  ...getApplicableSeasonalAdjustments(seasonalAdjustments, date, availableDateRanges, ticketTypeId)
]

// Price of each ticket type on `date`: its base price plus the day and seasonal adjustments that
// apply to that type (every adjustment for all tickets, and those that selected it), summed off
// the base like private prices. Without adjustments these are the base prices.
// { [ticketTypeId]: price }
export const getTicketRates = ({ ticketTypes = [], date, dayAdjustments, seasonalAdjustments, availableDateRanges }) =>
  Object.fromEntries(
    ticketTypes.map((ticketType) => [
      ticketType.id,
      applyPriceAdjustments(
        ticketType.price,
        getApplicableTicketAdjustments({ dayAdjustments, seasonalAdjustments, availableDateRanges, date, ticketTypeId: ticketType.id })
      )
    ])
  )

/**
 * Prices one ticketed booking: a line per ticket type ("ticket" kind, with ticketTypeId and
 * title), then add-ons, discount and fees exactly as calculateWidgetBookingTotal does.
 *
 * @param {object} params
 * @param {Array}  params.ticketTypes   the trip's ticket_types ({ id, title, price })
 * @param {object} params.ticketCounts  { [ticketTypeId]: count }
 * The other params are calculateWidgetBookingTotal's.
 * @returns {{ total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal }}
 */
export const calculateTicketedBookingTotal = ({
  ticketTypes = [],
  ticketCounts = {},
  date,
  addOnSelections = {},
  tripAddOns = [],
  dayAdjustments,
  seasonalAdjustments,
  availableDateRanges,
  customFees = [],
  discountPromos = [],
  cardFeePassthroughEnabled = false,
  depositFraction = 1,
  cardFeeShare = 1
}) => {
  const rates = getTicketRates({ ticketTypes, date, dayAdjustments, seasonalAdjustments, availableDateRanges })
  let guestTotal = 0
  const guestBreakdown = []
  for (const ticketType of ticketTypes) {
    const count = Number(ticketCounts?.[ticketType.id] || 0)
    if (count > 0) {
      const value = count * rates[ticketType.id]
      guestTotal += value
      guestBreakdown.push(
        buildLine({ kind: "ticket", label: `${ticketType.title} x ${count}`, value, count, ticketTypeId: ticketType.id, title: ticketType.title })
      )
    }
  }
  return addAddOnsDiscountAndFees({
    guestTotal,
    guestBreakdown,
    addOnSelections,
    tripAddOns,
    customFees,
    discountPromos,
    cardFeePassthroughEnabled,
    depositFraction,
    cardFeeShare,
    isCardFeeListed: true
  })
}

// Deposit / balance split shown under the total. The card fee is collected up front with the
// deposit, so the remaining balance carries no card fee.
export const computeDepositSplit = ({ preCardTotal = 0, cardProcessingFee = 0, depositPercent = 0 }) => {
  const safePreCardTotal = Math.max(0, preCardTotal)
  const depositBase = (safePreCardTotal * (depositPercent || 0)) / 100
  return {
    depositAmount: depositBase + Math.max(0, cardProcessingFee),
    balanceAmount: safePreCardTotal - depositBase
  }
}

// The card fee's fixed part ($0.30, or $0.49 for a PayPal-only guide) is charged once per order
// (client, Q33). Each booking of an order carries a share of it in proportion to its pre-card total,
// so the bookings' card fees add up to one fee on the order. Shares of the fixed fee, in the
// bookings' order.
export const getFixedCardFeeShares = (preCardTotals) => {
  const amounts = preCardTotals.map((value) => Math.max(0, Number(value) || 0))
  const sum = amounts.reduce((total, value) => total + value, 0)
  return amounts.map((value) => (sum > 0 ? value / sum : 1 / amounts.length))
}
